import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { EmptyState, AppButton } from "../ui";
import { getCurrentUser } from "../../utils/auth";
import { hasAnyPermission } from "../../utils/permissions";
import { can, keysFor, permissionLabel } from "../../utils/access";
import { pageAccessFor } from "../../data/pageAccess";

/**
 * Keeps an admin off an add / edit page their permissions do not allow
 * (src/data/pageAccess.js), with a sentence instead of a form whose save the
 * server would refuse. Only staff admins are checked: the owner and full-access
 * staff hold everything, and other roles never reach these pages.
 *
 * Mounted once in Layout, around the page.
 */
const AdminPageGuard = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  if (user?.usertype !== "admin") return children;

  const rule = pageAccessFor(pathname);
  if (!rule) return children;
  const allowed = rule.can ? can(rule.can) : hasAnyPermission(...rule.any);
  if (allowed) return children;

  const keys = rule.can ? keysFor(rule.can) : rule.any;
  const names = keys.map((k) => `“${permissionLabel(k)}”`).join(" or ");
  return (
    <div className="tw-py-10">
      <EmptyState
        icon={Lock}
        title="You can view this area, but not change it"
        description={`This page needs ${names}. Ask your super admin if you should have it.`}
        action={<AppButton variant="outline" onClick={() => navigate(-1)}>Go back</AppButton>}
      />
    </div>
  );
};

export default AdminPageGuard;
